/**
 * Leaderboard helpers (formatting / ranking math).
 * Live mode uses API/socket rows. Static mode (`REACT_APP_USE_STATIC_DATA`) uses LEADERBOARD_SEED.
 */
export const INITIAL_BALANCE = 10000;

/** Seed rows for frontend-only static mode. */
export const LEADERBOARD_SEED = [
  { id: 1, trader: "Alex Morgan", nation: "GB", equity: 28432.98 },
  { id: 2, trader: "Priya Shah", nation: "IN", equity: 25120.55 },
  { id: 3, trader: "Kenji Sato", nation: "JP", equity: 22810.12 },
  { id: 4, trader: "Sofia Rossi", nation: "IT", equity: 19480.4 },
  { id: 5, trader: "Marcus Lee", nation: "US", equity: 17105.77 },
  { id: 6, trader: "Amara Okoye", nation: "NG", equity: 14840.2 },
  { id: 7, trader: "Hans Mueller", nation: "DE", equity: 13290.15 },
  { id: 8, trader: "Elena Petrova", nation: "RU", equity: 11876.88 },
  { id: 9, trader: "Lucas Martin", nation: "FR", equity: 10921.33 },
  { id: 10, trader: "Chen Wei", nation: "CN", equity: 10264.7 },
  { id: 11, trader: "Yuki Tanaka", nation: "JP", equity: 9810.5 },
  { id: 12, trader: "Nora Lindqvist", nation: "SE", equity: 9488.25 },
  { id: 13, trader: "Omar Hassan", nation: "EG", equity: 9154.1 },
  { id: 14, trader: "Isabella Costa", nation: "BR", equity: 8750.4 },
  { id: 15, trader: "Daniel Kim", nation: "KR", equity: 8320.9 },
  { id: 16, trader: "Fatima Al-Rashid", nation: "SA", equity: 7920.15 },
  { id: 17, trader: "Mateo Alvarez", nation: "ES", equity: 7480.6 },
  { id: 18, trader: "Aisha Diallo", nation: "SN", equity: 6920.55 },
  { id: 19, trader: "Tom Hughes", nation: "AU", equity: 6280.33 },
  { id: 20, trader: "Nina Volkov", nation: "UA", equity: 5620.1 },
];

export const LEADERBOARD = [];

/** Alternating slot presentation — shadows stay on odd positions, not on moving rows. */
export const LEADERBOARD_SLOT_CLASSES = {
  odd: "data-table__slot--odd",
  even: "data-table__slot--even",
};

export const LEADERBOARD_ROW_STYLES = {
  odd: {
    backgroundColor: "#F4FAFF",
    boxShadow: "0px 20px 80px 0px #2E61FD1A",
  },
  even: {
    backgroundColor: "#FFFFFF",
  },
};

export const getLeaderboardSlotClassName = (index) =>
  index % 2 === 0 ? LEADERBOARD_SLOT_CLASSES.odd : LEADERBOARD_SLOT_CLASSES.even;

export const formatCurrency = (value) => {
  const abs = Math.abs(value).toLocaleString("en-US", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
  return `$${abs}`;
};

export const formatSignedCurrency = (value) => {
  const sign = value >= 0 ? "+" : "-";
  return `${sign}${formatCurrency(value)}`;
};

export const formatPercent = (value) =>
  `${value.toLocaleString("en-US", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}%`;

export const formatOrdinal = (n) => {
  const v = n % 100;
  if (v >= 11 && v <= 13) return `${n}th`;
  switch (n % 10) {
    case 1:
      return `${n}st`;
    case 2:
      return `${n}nd`;
    case 3:
      return `${n}rd`;
    default:
      return `${n}th`;
  }
};

/**
 * Performance (%) = ((Current Equity - Initial Balance) / Initial Balance) × 100
 */
export const calcPerformance = (equity, initialBalance = INITIAL_BALANCE) =>
  ((equity - initialBalance) / initialBalance) * 100;

export const calcProfitLoss = (equity, initialBalance = INITIAL_BALANCE) =>
  equity - initialBalance;

export const buildLeaderboardRows = (
  participants,
  initialBalance = INITIAL_BALANCE
) =>
  [...participants]
    .map((p) => {
      const profitLoss = calcProfitLoss(p.equity, initialBalance);
      const performance = calcPerformance(p.equity, initialBalance);

      return {
        ...p,
        profitLoss,
        performance,
        equityLabel: formatCurrency(p.equity),
        profitLossLabel: formatCurrency(profitLoss),
        marginLabel: formatPercent(performance),
      };
    })
    .sort((a, b) => b.performance - a.performance)
    .map((row, index) => ({
      ...row,
      rank: formatOrdinal(index + 1),
      rankNumber: index + 1,
    }));

/** Simulate a live trading tick — volatile enough for frequent multi-rank jumps. */
export const tickEquity = (equity, initialBalance = INITIAL_BALANCE) => {
  const performance = (equity - initialBalance) / initialBalance;
  const isBigMove = Math.random() < 0.22;

  // Slight mean reversion, but close to 50/50 so ranks swap both ways often.
  const upChance = isBigMove
    ? 0.5
    : performance > 1
      ? 0.42
      : performance > 0.25
        ? 0.47
        : performance > 0
          ? 0.5
          : 0.55;

  const direction = Math.random() < upChance ? 1 : -1;
  const magnitude = isBigMove
    ? 0.05 + Math.random() * 0.12
    : 0.015 + Math.random() * 0.05;
  const next = equity * (1 + magnitude * direction);

  return Math.max(500, Number(next.toFixed(2)));
};
